import React, { Component } from "react";
import { Mutation } from "react-apollo";
import gql from "graphql-tag";
import toPairs from "lodash/toPairs";
import groupBy from "lodash/groupBy";
import get from "lodash/get";
import {
  Box,
  Flex,
  FlexItem,
  Button,
  Popover,
  Text,
  createStyledComponent,
  withTheme,
} from "mineral-ui";
import Link, { ExternalLink } from "../components/Link";
import { H2, H3, H4, P, Ul } from "../components/html";
import { Plan, PlanName, PlanDescription } from "../components/billing";
import { parseLicense, timeRemaining } from "../license";

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const BetaBadge = createStyledComponent(Text, ({ theme }) => ({
  backgroundColor: theme.color_theme_60,
  color: theme.color_white,
  fontSize: "0.7rem",
  padding: "3px 8px",
  borderRadius: "1em",
  marginLeft: "6px",
  display: "inline-block",
  verticalAlign: "middle",
}));

const FlexFill = props => (
  <Flex style={{ minHeight: "100%", minWidth: "100%" }} {...props} />
);

const GetRepo = ({ repo }) => (
  <Mutation
    mutation={gql`
      mutation GetRepoMutation($repoId: Int!) {
        createUserRepo(input: { userRepo: { repoId: $repoId } }) {
          repoByRepoId {
            nodeId
            viewerStatus
          }
        }
      }
    `}
  >
    {createUserRepo => (
      <div className="">
        <Button
          primary
          fullWidth
          onClick={() => {
            createUserRepo({ variables: { repoId: repo.id } });
          }}
        >
          Accept EULA and get plugin
        </Button>
      </div>
    )}
  </Mutation>
);

class NpmCommands extends Component {
  state = {
    token: null,
  };

  render() {
    const { repo } = this.props;
    const license = this.state.token ? parseLicense(this.state.token) : null;
    return (
      <div>
        <P>
          You are responsible for keeping this license key confidential - it is
          not to be used outside of your organization.
        </P>
        {this.state.token ? (
          <div>
            <P>
              You must make the secret <tt>GRAPHILE_LICENSE</tt> environmental
              variable available to your server:
            </P>
            <Box paddingLeft="md">
              <P>
                <code style={{ wordBreak: "break-all" }}>
                  export GRAPHILE_LICENSE="
                  {this.state.token}"
                </code>
              </P>
              <P>
                <em>
                  (Licensed to <strong>{license.to}</strong>; license token will
                  need renewal before {new Date(license.exp).toISOString()} (
                  {timeRemaining(license.exp)})
                </em>
              </P>
            </Box>
            <P>
              Assuming you have <tt>postgraphile</tt> installed locally (not
              globally) you may install this bundle using <tt>yarn</tt>:
            </P>
            <Box paddingLeft="md">
              <P>
                <code style={{ wordBreak: "break-all" }}>
                  yarn add {repo.moduleName}
                </code>
              </P>
            </Box>
            <Box>
              <P>
                or with <tt>npm</tt>:
              </P>
            </Box>
            <Box paddingLeft="md">
              <P>
                <code style={{ wordBreak: "break-all" }}>
                  npm install --save {repo.moduleName}
                </code>
              </P>
            </Box>
            <P>Then you may run postgraphile as:</P>
            <Box paddingLeft="md">
              <P>
                <code>npx postgraphile --plugins {repo.moduleName} -c ...</code>
              </P>
            </Box>
            <P>
              More usage instructions can be found in the PostGraphile{" "}
              <Link href="https://graphile.org/postgraphile/plugins/">
                plugin documentation
              </Link>
              .
            </P>
          </div>
        ) : (
          <Mutation
            mutation={gql`
              mutation GetAccessToken {
                getAccessToken(input: {}) {
                  string
                }
              }
            `}
          >
            {getAccessToken => (
              <Button
                primary
                fullWidth
                onClick={async () => {
                  for (let attempts = 1; attempts <= 10; attempts++) {
                    const data = await getAccessToken();
                    if (data.data.getAccessToken.string) {
                      this.setState({ token: data.data.getAccessToken.string });
                      break;
                    } else {
                      // Try again
                      await sleep(Math.min(500, attempts * 100));
                    }
                  }
                }}
              >
                Reveal license key and instructions
              </Button>
            )}
          </Mutation>
        )}
      </div>
    );
  }
}

const PostGraphileSupporterDescription = () => (
  <div>
    <H4>Recently open-sourced</H4>
    <P>No need to purchase this currently, instead use PostGraphile v4.4.0+</P>
    <Ul>
      <li>
        <ExternalLink href="http://graphile.org/postgraphile/subscriptions/">
          GraphQL Subscriptions
        </ExternalLink>
      </li>
    </Ul>
  </div>
);

const PostGraphileProDescription = () => (
  <div>
    <H4>Enhancements for production usage.</H4>
    <P>
      Advanced functionality and protections for PostGraphile which are
      particularly useful if you're using PostGraphile in production.
    </P>
    <Ul>
      <li>
        <ExternalLink href="https://graphile.org/postgraphile/production/#experimental-graphql-cost-limit">
          Query cost limit (experimental)
        </ExternalLink>
      </li>
      <li>
        <ExternalLink href="https://graphile.org/postgraphile/production/#implementing-pagination-caps">
          Enforced pagination limits
        </ExternalLink>
      </li>
      <li>
        <ExternalLink href="https://graphile.org/postgraphile/production/#running-postgraphile-in-production">
          Prevent deeply nested queries
        </ExternalLink>
      </li>
      <li>
        <ExternalLink href="https://graphile.org/postgraphile/production/#sending-queries-to-read-replicas">
          Separate connection for read-only queries
        </ExternalLink>
      </li>
    </Ul>
  </div>
);

/*
const PostGraphileProDescription_verbose = () => (
  <div>
    <p>
      This plugin offers some advanced functionality and protections for
      PostGraphile which are particularly useful if you're using PostGraphile in
      production.
    </p>
    <dl>
      <dt>Enforce pagination limits</dt>
      <dd>
        Requires that GraphQL clients specify <tt>before</tt>/<tt>after</tt> on
        all connections, preventing users from accidentally requesting the
        entire contents of a table. Enable via <tt>--default-pagination-cap</tt>;
        specific connections can be overriden by adding a smart comment to the
        table with a tag like <tt>@paginationCap 50</tt>
      </dd>
      <dt>Separate connection for read-only queries</dt>
      <dd>
        By default <tt>--connection</tt> can be used for both queries and
        mutations; however if you have read replicas set up you might want to
        use them for queries instead in order to reduce the load on your master
        database. You can do this using <tt>--read-only-connection</tt>, passing
        a PostgreSQL connection string. We do not currently support multiple
        hosts in this string (you can use{" "}
        <a href="http://www.pgpool.net/mediawiki/index.php/Main_Page">
          <tt>pgPool</tt>
        </a>{" "}
        or similar to achieve this)
      </dd>
      <dt>Prevent deeply nested queries</dt>
      <dd>
        Via <tt>--graphql-depth-limit 16</tt> you can ensure that any GraphQL
        request that attempts to fetch fields more than 16 levels deep will be
        discarded
      </dd>
      <dt>Query cost limit</dt>
      <dd>
        Calculates a rough cost for each GraphQL query and discards any queries
        that are over the threshold set by `--graphql-cost-limit`. (Warning:
        this feature is highly experimental, we are constantly iterating the
        values that influence the cost of the query; if you want to use this in
        production make sure that you can easily change the cost limit.)
      </dd>
    </dl>
    <p>
      All plans that include this plugin also include the PostGraphile Supporter
      Plugin, so you can have subscriptions too
    </p>
  </div>
);
*/

const BetaContent = () => (
  <Box style={{ maxWidth: "20em" }}>
    <P fontWeight="regular" inherit={false}>
      <strong>Now in beta testing!</strong> We're still smoothing the edges, but
      dive in! Some of the defaults may change, so it's wise to pin down all the
      settings you're using once you're happy with them.
    </P>
    <P fontWeight="regular" inherit={false}>
      Please send any feedback you have, so we can factor it into the final
      release. Hang out in the vip-lounge on{" "}
      <a href="http://discord.gg/graphile">Discord</a> for the latest news.
    </P>
  </Box>
);

const RepoFlexItem = createStyledComponent(FlexItem, ({ theme }) => ({
  border: `1px solid ${theme.borderColor}`,
  flexBasis: "10em",
}));

const Repo = withTheme(({ viewer, repo, currency, theme, isAlpha }) => {
  const existingPlan = get(
    repo.planRepos.nodes.find(({ plan }) => plan.viewerHasPlan),
    "plan"
  );

  let bottomBgColor = theme.backgroundColor_theme_selectedHover;

  if (repo.viewerStatus === "ACTIVE") {
    bottomBgColor = theme.color_theme_20;
  } else if (repo.viewerStatus === "FROZEN") {
    bottomBgColor = theme.backgroundColor_danger_active;
  } else if (repo.viewerStatus === "PENDING") {
    bottomBgColor = theme.color_theme_20;
  } else if (repo.viewerStatus === null && existingPlan) {
    bottomBgColor = theme.color_theme_20;
  } else if (repo.viewerStatus === null) {
    bottomBgColor = theme.color_gray_30;
  }

  return (
    <RepoFlexItem margin="sm" grow={1} shrink={0}>
      <FlexFill direction="column">
        <FlexItem
          padding="lg"
          style={{ backgroundColor: theme.color_theme_10 }}
        >
          <H3 color={theme.color_theme}>
            {repo.name}
            {isAlpha ? (
              <Popover content={<BetaContent />}>
                <BetaBadge>
                  BETA <span className="fa fa-info-circle" />
                </BetaBadge>
              </Popover>
            ) : null}
          </H3>
          {repo.slug === "postgraphile-supporter" ? (
            <PostGraphileSupporterDescription />
          ) : repo.slug === "postgraphile-pro" ? (
            <PostGraphileProDescription />
          ) : (
            <p>{repo.description}</p>
          )}
          <P small>
            Use of this plugin is governed by the{" "}
            <Link target="_blank" href={`/eulas/${repo.slug}/`}>
              {repo.name} EULA
            </Link>
            .
          </P>
        </FlexItem>
        <FlexItem
          grow={1}
          padding="lg"
          style={{
            backgroundColor: bottomBgColor,
          }}
        >
          {repo.viewerStatus === "ACTIVE" && (
            <div className="flex-auto  bg-light-green ph3">
              <H4>License key and instructions </H4>
              <NpmCommands viewer={viewer} repo={repo} />
            </div>
          )}
          {repo.viewerStatus === "FROZEN" && (
            <div className="flex-auto  bg-orange pa3">
              This repo is not receiving updates, please check your subscription
              status and if you think this is an error get in touch!
            </div>
          )}
          {repo.viewerStatus === "PENDING" && (
            <div className="flex-auto  bg-lightest-blue pa3">
              <H4>Preparing your plugin</H4>
              <P>Generating a license key for you, please wait...</P>
            </div>
          )}
          {repo.viewerStatus === null &&
            (existingPlan ? (
              <div className="flex-auto  bg-lightest-blue pa3">
                <H4>
                  You are on the{" "}
                  <PlanName prefix={existingPlan.slug.split("-")[0]} />
                </H4>
                <P>
                  The <PlanName prefix={existingPlan.slug.split("-")[0]} />{" "}
                  includes access to this plugin. To get your license key and
                  instructions, click the button below:
                </P>
                <div className="mv3 mb4 tc-xs">
                  <GetRepo repo={repo} />
                </div>
              </div>
            ) : (
              <div className="flex-auto  bg-lightest-blue pa3 pv1">
                <H3>Available in these plans:</H3>
                {toPairs(
                  groupBy(
                    repo.planRepos.nodes.filter(n => n.plan.id < 2000),
                    pr => pr.plan.slug.split("-")[0]
                  )
                ).map(([planPrefix, nodes]) => (
                  <Box key={planPrefix} marginTop="xl">
                    <H4>
                      <PlanName prefix={planPrefix} />:
                    </H4>
                    <P>
                      <PlanDescription prefix={planPrefix} />
                    </P>
                    <Box paddingLeft="md">
                      {nodes
                        .filter(
                          planRepo =>
                            !planRepo.plan.currency ||
                            !currency ||
                            planRepo.plan.currency === currency
                        )
                        .map(planRepo => (
                          <P key={planRepo.nodeId} noMargins>
                            <Plan plan={planRepo.plan} />
                          </P>
                        ))}
                    </Box>
                    <Button element={Link} to="/settings">
                      Change your plan »
                    </Button>
                  </Box>
                ))}
              </div>
            ))}
        </FlexItem>
      </FlexFill>
    </RepoFlexItem>
  );
});

class Home extends Component {
  state = {
    currency: "USD",
  };

  componentWillUnmount() {
    this.stopPolling();
  }

  handleCurrencyChange = e => {
    this.setState({ currency: e.target.value || "USD" });
  };

  startPolling() {
    if (!this.timeout) {
      this.delay = 100;
      this.timeout = setTimeout(this.poll, this.delay);
    }
  }

  stopPolling() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  poll = () => {
    this.props.data.refetch().then(() => {
      if (this.timeout) {
        this.delay = Math.min(2500, this.delay * 1.5);
        this.timeout = setTimeout(this.poll, this.delay);
      }
    });
  };

  render() {
    const { data } = this.props;
    // Oughtn't do this during render ;)
    if (
      data.allRepos &&
      data.allRepos.nodes.some(repo => repo.viewerStatus === "PENDING")
    ) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
    return (
      <Box>
        <Box marginVertical="xl">
          <H2 align="center">Powerful PostGraphile Plugins</H2>
          <H4 align="center">
            Increase developer productivity with powerful features and solve
            production concerns.
          </H4>
        </Box>
        <Flex breakpoints={["wide"]} direction={["column", "row"]}>
          {data && data.allRepos
            ? data.allRepos.nodes.map(repo =>
                repo.id !== 1 || repo.viewerStatus === "ACTIVE" ? (
                  <Repo key={repo.nodeId} viewer={data.viewer} repo={repo} />
                ) : null
              )
            : "One moment please..."}
        </Flex>
        <Box marginTop="lg">
          <P>
            After taking out a Patreon plan, you must{" "}
            <Link href="/auth/patreon">log in again</Link> for the plan to be
            reflected.
          </P>
          <P>
            After taking out a GitHub Sponsors plan, you must{" "}
            <Link href="/auth/github-sponsors">log in again</Link> for the plan
            to be reflected.
          </P>
          <P small>
            Prices shown are exclusive of VAT; purchasers within the UK will pay
            VAT at 20%, purchasers within the EU must supply a VAT registration
            number to avoid being charged VAT, people not resident within the EU
            will not pay VAT.
          </P>
        </Box>
      </Box>
    );
  }
}
export default withTheme(Home);
